import { ArrayShape, CallExpressionShape, ObjectShape, Plugin } from '@servicenow/sdk-build-core'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import {
    getPlaybookProcessTypeSysId,
    PLAYBOOK_API_NAME,
    PLAYBOOK_KNOWN_PROCESS_TYPE_NAMES,
    isPlaybookProcessTypeSysId,
    PARENT_RECORD_MAPPER_KEY,
} from '../constants/playbook-plugin-constants'

const MAX_TRIGGER_COUNT = 10

/**
 * Validates PlaybookDefinition calls and emits diagnostics for invalid configurations.
 *
 * This plugin runs validation checks on PlaybookDefinition calls and reports errors for:
 * - Setting processType to a non-name value (for example a sys_id or Now.ID reference)
 * - Empty parentTable value
 * - Missing parentRecord on triggers whose table differs from parentTable
 * - Exceeding the maximum allowed number of triggers per playbook (10)
 *
 * Note: This plugin returns { success: false } to indicate it is for diagnostics only
 * and does not create records.
 */
export const PlaybookDiagnosticsPlugin = Plugin.create({
    name: 'PlaybookDiagnosticsPlugin',

    shapes: [
        {
            shape: CallExpressionShape,
            fileTypes: ['fluent'],

            toRecord(callExpression, { diagnostics }) {
                if (callExpression.getCallee() !== PLAYBOOK_API_NAME) {
                    return { success: false }
                }

                const config = callExpression.getArgument(0)?.asObject()
                if (!config) {
                    return { success: false }
                }

                // --- Validate processType ---
                // processType must be one of the SDK-supported mapped names.
                // Do not add arbitrary/custom playbook types in Fluent without explicit
                // SDK support work: each supported type may require its own feature-flag
                // modeling, diagnostics, and transform/build handling.
                const processTypeShape = config.get('processType')
                const processTypeExamples = PLAYBOOK_KNOWN_PROCESS_TYPE_NAMES.map((value) => `"${value}"`).join(', ')

                // isDefined() checks the key is present; isUndefined() checks the value is
                // explicitly `undefined`. Both are needed: isDefined() alone would match a
                // key with value `undefined`, and isUndefined() alone would not confirm presence.
                if (processTypeShape?.isDefined() && !processTypeShape.isUndefined()) {
                    const processTypeValue = processTypeShape.ifString()?.getValue()
                    if (processTypeValue === undefined) {
                        diagnostics.error(
                            processTypeShape,
                            `processType must be a name string. Use a process type name such as ${processTypeExamples}.`
                        )
                    } else if (processTypeValue.trim() === '') {
                        diagnostics.error(
                            processTypeShape,
                            `processType must be a non-empty name string. Use a process type name such as ${processTypeExamples}.`
                        )
                    } else if (isPlaybookProcessTypeSysId(processTypeValue)) {
                        diagnostics.error(
                            processTypeShape,
                            `processType must be a name, not a sys_id. Use a process type name such as ${processTypeExamples}.`
                        )
                    } else if (!getPlaybookProcessTypeSysId(processTypeValue)) {
                        diagnostics.error(
                            processTypeShape,
                            `Unsupported processType "${processTypeValue}". We do not have that process type defined in the SDK mapping. Supported values: ${processTypeExamples}.`
                        )
                    }
                }

                // Future: add type-specific diagnostics here as features are implemented.
                // New playbook types are not plug-and-play: they may support a different
                // set of capabilities (for example publicAccess, decisions, stages,
                // or optional activities), and each supported type needs explicit
                // validation before we allow it in Fluent.

                // --- Validate parentTable ---
                const parentTableShape = config.get('parentTable')
                const parentTableVal = parentTableShape?.ifString()?.getValue()
                if (parentTableShape && parentTableVal === '') {
                    diagnostics.error(parentTableShape, 'parentTable must be a valid table name.')
                }

                // Triggers live in the 2nd-arg object. Extract the triggers array.
                const triggersObject = callExpression.getArgument(1)
                if (triggersObject && !triggersObject.isUndefined() && triggersObject.is(ObjectShape)) {
                    const triggersValue = triggersObject.get('triggers')
                    if (triggersValue && triggersValue.is(ArrayShape)) {
                        const triggers = triggersValue.getElements()

                        // Validate max trigger count
                        if (triggers.length > MAX_TRIGGER_COUNT) {
                            diagnostics.error(
                                triggersValue,
                                `Playbook has ${triggers.length} triggers. The maximum allowed is ${MAX_TRIGGER_COUNT}.`
                            )
                        }

                        // Validate parentRecord on triggers when parentTable is set and non-empty.
                        // Fires in two cases:
                        //   1. Trigger has a table different from parentTable.
                        //   2. Trigger has no table (scheduled triggers) — parent linking still
                        //      requires parentRecord because the trigger cannot supply one.
                        // parentRecord is provided in the 4th-arg mapper callback
                        if (parentTableVal) {
                            for (const element of triggers) {
                                if (!element.is(CallExpressionShape)) {
                                    continue
                                }
                                const triggerCall = element.as(CallExpressionShape)
                                const triggerInputs = triggerCall.getArgument(2)?.ifObject()?.asObject()
                                if (!triggerInputs) {
                                    continue
                                }

                                const triggerTable = triggerInputs.get('table')?.ifString()?.getValue()

                                if (triggerTable !== parentTableVal) {
                                    // Check 4th-arg mapper for parentRecord
                                    const mapperArg = triggerCall.getArgument(3)
                                    let hasParentRecord = false
                                    if (mapperArg && !mapperArg.isUndefined() && mapperArg.is(ArrowFunctionShape)) {
                                        const returnVal = mapperArg.as(ArrowFunctionShape).getReturnValue()
                                        if (returnVal?.is(ObjectShape)) {
                                            const pr = returnVal.as(ObjectShape).get(PARENT_RECORD_MAPPER_KEY)
                                            hasParentRecord = !!pr && !pr.isUndefined()
                                        }
                                    }

                                    if (!hasParentRecord) {
                                        const context = triggerTable
                                            ? `Trigger table '${triggerTable}' differs from`
                                            : 'Scheduled trigger has no record table matching'
                                        diagnostics.error(
                                            triggerInputs,
                                            `${context} playbook parentTable '${parentTableVal}'. parentRecord is required in the trigger's mapper callback.`
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                // Don't create a record - this is diagnostics only
                return { success: false }
            },
        },
    ],
    docs: [],
})
